/**
 * article-product-tooltip.js
 *
 * 2026-09-24: own request -- "can we add the tooltip we have in the cart on
 * complete your routine upsell that shows the product info price ml
 * description. can we add that to show on any product links in any article
 * on hover on desktop?"
 *
 * The cart's own tooltip (cart-routine-upsell.liquid / cart.css) is built
 * from a Liquid `product` object already in scope for one specific card --
 * that doesn't exist here. Article body copy is plain hand-authored HTML
 * with ordinary <a href="/products/..."> links scattered through running
 * prose, on 49 existing articles (and any future one) with no structured
 * product reference to hook a server-side render onto. Rather than
 * migrating every article's body to add that structure, this fetches each
 * linked product's real data client-side from Shopify's own public
 * `/products/<handle>.js` JSON endpoint (no auth, no theme changes needed
 * per-article) the first time its link is hovered, caches it by handle
 * (an article can link the same product more than once), and shows a
 * single shared tooltip positioned next to whichever link is active.
 *
 * Desktop-only by construction: gated on `(hover: hover)` -- same
 * reasoning as the cart tooltip (own request explicitly said "on hover on
 * desktop", and touch has no hover to trigger this from regardless).
 *
 * 2026-09-24 (later): own follow-up -- "does the product hover tooltip
 * show the review stars like the add to cart version? i am not seeing
 * those come through". Judge.me's badge (product.metafields.judgeme.badge,
 * what the cart tooltip echoes) isn't part of the `/products/<handle>.js`
 * JSON endpoint at all -- confirmed live by fetching a real product's page
 * HTML instead: Judge.me renders it as fully static markup at page-build
 * time (`<div class="jdgm-prev-badge" data-average-rating="..."
 * data-number-of-reviews="..." style="display:none"><span
 * class="jdgm-prev-badge__stars">...5 star spans...</span><span
 * class="jdgm-prev-badge__text">N reviews</span></div>` -- no live script
 * call needed to populate the stars, they're already in the HTML). So a
 * second, separate fetch (the product's own page, not the .js endpoint)
 * pulls that same markup out via regex and reuses it directly -- see
 * fetchBadge() -- rather than trying to reimplement Judge.me's own
 * rendering or guess at an undocumented API. The `display:none` inline
 * style is removed here ourselves (rather than relying on Judge.me's own
 * script noticing this dynamically-inserted copy) only when there's a
 * real review count, matching the same "hide with zero reviews" behaviour
 * used everywhere else on the site.
 */
(function () {
  if (!window.matchMedia('(hover: hover)').matches) return;

  const BLANK_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  const cache = new Map();
  let activeLink = null;
  let showTimer = null;
  let tooltipEl = null;

  function buildTooltip() {
    if (tooltipEl) return tooltipEl;
    tooltipEl = document.createElement('div');
    tooltipEl.className = 'article-product-tooltip';
    tooltipEl.setAttribute('role', 'tooltip');
    tooltipEl.innerHTML =
      '<img class="article-product-tooltip__image" alt="" loading="lazy">' +
      '<div class="article-product-tooltip__body">' +
      '<p class="article-product-tooltip__title"></p>' +
      '<div class="article-product-tooltip__badge" hidden></div>' +
      '<p class="article-product-tooltip__description"></p>' +
      '<div class="article-product-tooltip__meta">' +
      '<span class="article-product-tooltip__price"></span>' +
      '<span class="article-product-tooltip__volume"></span>' +
      '</div></div>';
    document.body.appendChild(tooltipEl);
    return tooltipEl;
  }

  // Mirrors product-volume.liquid's unit formatting so the tooltip matches
  // the same "50ml" style shown everywhere else on the site.
  function formatVolume(variant) {
    const upm = variant && variant.unit_price_measurement;
    if (!upm || !upm.quantity_value) return '';
    const qty = String(upm.quantity_value).replace(/\.0$/, '');
    const units = { ML: 'ml', CL: 'cl', L: 'l', MG: 'mg', G: 'g', KG: 'kg' };
    const unit = units[upm.quantity_unit] || (upm.quantity_unit || '').toLowerCase();
    // 2026-10-04: count units (e.g. eye masks) -- the pack label lives in a
    // product metafield this JSON doesn't carry, so show nothing rather than "7item".
    if (unit === 'item') return '';
    return qty + unit;
  }

  function formatMoney(cents) {
    // Shopify's public product.js prices are in the shop's currency's
    // smallest unit (e.g. pence). FoxTheme.Currency.formatMoney is this
    // theme's own sitewide helper (theme.js), using the real shop money
    // format already exposed as FoxTheme.settings.moneyFormat
    // (js-variables.liquid) -- same two things every other price on the
    // site is formatted with, not a one-off implementation here.
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.settings) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }
    return '£' + (cents / 100).toFixed(2);
  }

  const badgeCache = new Map();

  function parseBadge(html) {
    const match = /<div[^>]*class=['"][^'"]*jdgm-prev-badge[^'"]*['"][^>]*>/.exec(html);
    if (!match) return null;
    const openTag = match[0];
    const start = match.index;
    const closeIdx = html.indexOf('</div>', start);
    if (closeIdx === -1) return null;
    const countMatch = /data-number-of-reviews=['"](\d+)['"]/.exec(openTag);
    const count = countMatch ? parseInt(countMatch[1], 10) : 0;
    if (!count) return null; // matches sitewide "hide when zero reviews"
    let inner = html.slice(start, closeIdx + 6);
    // Strip the default inline display:none -- shown deliberately here
    // since there's a real review count, not left for a script to notice.
    inner = inner.replace(/style=(['"])display:\s*none\1/, '');
    return inner;
  }

  function fetchBadge(handle) {
    if (badgeCache.has(handle)) return Promise.resolve(badgeCache.get(handle));
    return fetch('/products/' + handle)
      .then((res) => (res.ok ? res.text() : ''))
      .then((html) => {
        const badge = parseBadge(html);
        badgeCache.set(handle, badge);
        return badge;
      })
      .catch(() => null);
  }

  function getHandle(link) {
    try {
      const path = new URL(link.href, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  function render(product) {
    const el = buildTooltip();
    // Cleared up front so a stale previous product's stars never flash
    // before this product's own badge fetch (a separate, independent
    // request from the product data fetch) resolves.
    const badgeEl = el.querySelector('.article-product-tooltip__badge');
    badgeEl.innerHTML = '';
    badgeEl.hidden = true;
    const variant = product.variants && product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]);
    const img = el.querySelector('.article-product-tooltip__image');
    if (image) {
      // 2026-09-26: own report -- the tooltip sometimes showed the previous
      // product's image for a moment before flicking to the current one.
      // The tooltip reuses one <img>, so the old picture stayed painted
      // until the new file finished loading. Drop it straight away for a
      // blank placeholder (skeleton shimmer in CSS), preload the new file
      // off-screen, and only swap it in once it has loaded and this
      // product is still the one being shown.
      const src = image.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1$2');
      img.hidden = false;
      if (img.dataset.src !== src) {
        img.dataset.src = src;
        img.classList.add('is-loading');
        img.src = BLANK_PIXEL;
        const loader = new Image();
        loader.onload = loader.onerror = () => {
          if (img.dataset.src !== src) return;
          img.src = src;
          img.classList.remove('is-loading');
        };
        loader.src = src;
      }
    } else {
      img.hidden = true;
      img.dataset.src = '';
    }
    el.querySelector('.article-product-tooltip__title').textContent = product.title;
    const description = (product.description || '').replace(/<[^>]*>/g, '').trim();
    const descEl = el.querySelector('.article-product-tooltip__description');
    descEl.textContent = description;
    descEl.hidden = !description;
    el.querySelector('.article-product-tooltip__price').textContent = variant ? formatMoney(variant.price) : '';
    const volumeText = formatVolume(variant);
    const volumeEl = el.querySelector('.article-product-tooltip__volume');
    volumeEl.textContent = volumeText;
    volumeEl.hidden = !volumeText;
  }

  function position(link) {
    const el = buildTooltip();
    const linkRect = link.getBoundingClientRect();
    // 2026-09-24, own bugfix -- the review-badge fetch (a separate, later-
    // arriving request, see show()) calls position() a second time to
    // account for the extra height once stars are in, and this measuring
    // step used to add/remove the --visible class around the
    // getBoundingClientRect() call below. That's unnecessary (the class
    // only toggles opacity/pointer-events, not display -- the element
    // already occupies its real layout box at rest) and was the actual
    // bug: this function's own class removal was the LAST thing to touch
    // the class on that second call, permanently clearing the visibility
    // renderAndShow had just set, right as the badge loaded. Measuring
    // without touching the class at all avoids the whole problem.
    const tipRect = el.getBoundingClientRect();

    const gap = 8;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    /* 2026-09-24: own follow-up -- "make sure it never overflows off the
       screen", after widening the tooltip. The previous version only
       checked "is there room above the link" and fell through to below
       unconditionally, with no check that BELOW also fits -- on a short
       viewport (or a link near the bottom of the page) that could still
       push the tooltip's own bottom edge past the viewport. Now picks
       whichever side (above/below) actually has enough room, and if
       NEITHER side fully fits (a very short viewport), clamps top to keep
       the tooltip's top and bottom both on-screen rather than trusting
       either side blindly. */
    const spaceAbove = linkRect.top - gap;
    const spaceBelow = viewportH - linkRect.bottom - gap;
    let top;
    if (tipRect.height <= spaceAbove) {
      top = linkRect.top - tipRect.height - gap;
    } else if (tipRect.height <= spaceBelow) {
      top = linkRect.bottom + gap;
    } else {
      // Neither side has full room -- pick the larger gap, then clamp.
      top = spaceAbove >= spaceBelow ? linkRect.top - tipRect.height - gap : linkRect.bottom + gap;
    }
    top = Math.min(Math.max(top, gap), viewportH - tipRect.height - gap);

    let left = linkRect.left;
    left = Math.min(left, viewportW - tipRect.width - gap);
    left = Math.max(left, gap);

    el.style.top = Math.round(top) + 'px';
    el.style.left = Math.round(left) + 'px';
  }

  function show(link) {
    const handle = getHandle(link);
    if (!handle) return;
    activeLink = link;

    const cached = cache.get(handle);
    if (cached) {
      renderAndShow(link, cached);
    } else {
      fetch('/products/' + handle + '.js')
        .then((res) => (res.ok ? res.json() : null))
        .then((product) => {
          if (!product) return;
          cache.set(handle, product);
          if (activeLink === link) renderAndShow(link, product);
        })
        .catch(() => {});
    }

    // Independent fetch/cache from the product data above -- the review
    // badge isn't in the .js endpoint at all (see file header), and
    // arriving a beat later than the rest of the tooltip is fine, it just
    // fades the stars in once ready rather than blocking everything else.
    fetchBadge(handle).then((badgeHtml) => {
      if (activeLink !== link) return;
      const el = buildTooltip();
      const badgeEl = el.querySelector('.article-product-tooltip__badge');
      badgeEl.innerHTML = badgeHtml || '';
      badgeEl.hidden = !badgeHtml;
      if (badgeHtml && el.classList.contains('article-product-tooltip--visible')) {
        position(link);
      }
    });
  }

  function renderAndShow(link, product) {
    render(product);
    position(link);
    buildTooltip().classList.add('article-product-tooltip--visible');
  }

  function hide() {
    activeLink = null;
    clearTimeout(showTimer);
    if (tooltipEl) tooltipEl.classList.remove('article-product-tooltip--visible');
  }

  function isProductLink(el) {
    return el && el.tagName === 'A' && /\/products\//.test(el.getAttribute('href') || '') && el.closest('.article__content');
  }

  document.addEventListener(
    'mouseover',
    (event) => {
      const link = event.target.closest('a');
      if (!isProductLink(link) || link === activeLink) return;
      clearTimeout(showTimer);
      showTimer = setTimeout(() => show(link), 150);
    },
    true
  );

  document.addEventListener(
    'mouseout',
    (event) => {
      const link = event.target.closest('a');
      if (!isProductLink(link)) return;
      if (event.relatedTarget && link.contains(event.relatedTarget)) return;
      hide();
    },
    true
  );

  document.addEventListener('focusin', (event) => {
    if (isProductLink(event.target)) show(event.target);
  });
  document.addEventListener('focusout', (event) => {
    if (isProductLink(event.target)) hide();
  });

  // A stale-positioned tooltip left over a scroll is worse than none --
  // simplest fix is to just hide on scroll rather than track position.
  window.addEventListener('scroll', hide, { passive: true });
})();
